import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtimeAdapter } from '@/contexts/RealtimeContext';

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  sender_username: string | null;
  sender_picture: string | null;
  sender_belt_rank: string;
  content: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  is_mine: boolean;
}

export const useMessages = (conversationId: string | null) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const realtimeAdapter = useRealtimeAdapter();

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: async () => {
      if (!conversationId || !user?.id) return [];
      const { data, error } = await supabase.rpc('get_messages', {
        p_conversation_id: conversationId,
        p_limit:           50,
        p_offset:          0,
      });
      if (error) {
        console.error('Error fetching messages:', error);
        return [];
      }
      return Array.isArray(data) ? (data as Message[]) : [];
    },
    enabled: !!conversationId && !!user?.id,
  });

  useEffect(() => {
    if (!conversationId || !user?.id) return;

    supabase
      .rpc('mark_conversation_as_read', { p_conversation_id: conversationId })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      });
  }, [conversationId, user?.id, queryClient]);

  const {
    mutateAsync: sendMessage,
    isPending: isSending,
    error: sendError,
  } = useMutation({
    mutationFn: async (content: string) => {
      if (!conversationId) throw new Error('No active conversation');
      const { data, error } = await supabase.rpc('send_message', {
        p_conversation_id: conversationId,
        p_content:         content,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  useEffect(() => {
    if (!conversationId || !user?.id) return;

    return realtimeAdapter.subscribeToConversation(
      conversationId,
      (payload) => {
        queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });

        if (payload.sender_id !== user.id) {
          supabase
            .rpc('mark_conversation_as_read', { p_conversation_id: conversationId })
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
            });
        }

        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      },
    );
  }, [conversationId, user?.id, queryClient, realtimeAdapter]);

  return {
    messages,
    isLoading,
    sendMessage,
    isSending,
    sendError,
  };
};
